import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import WebApp from '@twa-dev/sdk';
import useUserStore from '../stores/userStore';
import ReactPixel from 'react-facebook-pixel';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #23272f;
  color: #fff;
  padding-top: var(--tg-content-safe-area-inset-top, 0px);
  padding-bottom: var(--tg-content-safe-area-inset-bottom, 0px);
  overflow-y: auto;
`;

const Header = styled.div`
  padding: 20px;
  text-align: center;
`;

const Logo = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: #4b69ff;
  margin-bottom: 20px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #4b69ff;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 100px; /* Отступ для зафиксированной кнопки */
`;

const QuestionSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 30px 20px 10px 20px;
  min-height: 0;
`;

const Title = styled.h1`
  font-size: clamp(20px, 5vw, 28px);
  font-weight: bold;
  text-align: center;
  margin-top: 20px;
  margin-bottom: clamp(12px, 3vw, 20px);
  line-height: 1.2;
`;

const Description = styled.p`
  font-size: clamp(14px, 4vw, 16px);
  color: #ccc;
  text-align: center;
  margin-bottom: clamp(12px, 3vw, 20px);
  line-height: 1.5;
`;

const WelcomeImage = styled.img`
  max-width: min(70vw, 300px);
  max-height: 30vh;
  width: auto;
  height: auto;
  margin-bottom: clamp(12px, 3vw, 20px);
  border-radius: 16px;
`;

const ImageContainer = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns === 3 ? '1fr 1fr 1fr' : '1fr 1fr'};
  gap: clamp(12px, 4vw, 20px);
  max-width: min(90vw, 500px);
  width: 100%;
  margin: 0 auto;
  max-height: 60vh;
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.02);
  }
`;

const OptionImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
`;

const QuizSection = styled.div`
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const OptionButton = styled.button`
  padding: 16px 20px;
  border: 2px solid ${props => props.selected ? '#4b69ff' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 12px;
  background: ${props => props.selected ? 'rgba(75, 105, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    border-color: #4b69ff;
    background: rgba(75, 105, 255, 0.1);
  }
`;

const ImageOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 18px;
  border-radius: 16px;
  border: none;
  background: ${props => props.disabled ? 'rgba(75, 105, 255, 0.3)' : 'linear-gradient(135deg, #4b69ff, #6b7cff)'};
  color: #fff;
  font-size: 18px;
  font-weight: 800;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(75, 105, 255, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #3a56d4, #5a6bff);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(75, 105, 255, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(75, 105, 255, 0.3);
  }
`;

const TimerContainer = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Timer = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #ff6b6b;
  font-family: 'Courier New', monospace;
`;

const PersonalizedOfferCard = styled.div`
  background: ${props => {
		if (props.theme === 'ct') return 'linear-gradient(135deg, #1e3a8a, #3b82f6)';
		if (props.theme === 'neutral') return 'linear-gradient(135deg, #6b7280, #9ca3af)';
		return 'linear-gradient(135deg, #dc2626, #ef4444)';
	}};
  border-radius: 16px;
  padding: 16px;
  margin-top: 20px;
  margin-bottom: 16px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const OfferTitle = styled.h2`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 6px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const OfferSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const SelectedItemsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const SelectedItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SelectedItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: ${props => props.type === 'map' ? 'cover' : 'contain'};
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
`;

const SelectedItemLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  font-weight: 500;
`;

const PersonalizedBadge = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 8px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  font-weight: 500;
  backdrop-filter: blur(10px);
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  margin-top: 40px;
  position: relative;
`;

const DiscountBadge = styled.div`
  background: linear-gradient(135deg, #ff6b6b, #ff5252);
  color: #fff;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 800;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  position: absolute;
  top: -15px;
  right: -15px;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.2);
  transform: rotate(15deg);
  z-index: 10;
  animation: bounce 2s infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, #ff6b6b, #ff5252);
    border-radius: 25px;
    z-index: -1;
    opacity: 0.3;
    filter: blur(4px);
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: rotate(15deg) translateY(0); }
    40% { transform: rotate(15deg) translateY(-5px); }
    60% { transform: rotate(15deg) translateY(-3px); }
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const OriginalPrice = styled.span`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: line-through;
`;

const DiscountedPrice = styled.span`
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const StarIcon = styled.span`
  font-size: 20px;
  margin-left: 6px;
  color: #ffd700;
`;

const FragIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-left: 6px;
  vertical-align: middle;
`;

const PriceLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
`;

const FragsInfo = styled.div`
  font-size: 20px;
  color: #fff;
  text-align: center;
  margin-bottom: 16px;
  font-weight: 700;
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  background: rgba(0, 0, 0, 0.3);
  padding: 16px 20px;
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const OriginalPriceInText = styled.span`
  text-decoration: line-through;
  color: rgba(255, 255, 255, 0.4);
  font-size: 18px;
  font-weight: 500;
  opacity: 0.7;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: rgba(255, 255, 255, 0.6);
    transform: translateY(-50%);
  }
`;

const NewPrice = styled.span`
  color: #ffd700;
  font-size: 24px;
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const Arrow = styled.span`
  color: #ff6b6b;
  font-size: 20px;
  font-weight: 800;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  
  /* Когда только одна кнопка, она занимает всю ширину */
  &:has(> button:only-child) {
    button {
      flex: 1;
    }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  color: #fca5a5;
  font-size: 14px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const OnboardingQuiz = ({ onComplete }) => {
	const { t } = useTranslation();
	const fetchUser = useUserStore(state => state.fetchUser);
	const [currentStep, setCurrentStep] = useState(0);
	const [answers, setAnswers] = useState({});
	const [timeLeft, setTimeLeft] = useState(30000); // 30 секунд в миллисекундах
	const [timerActive, setTimerActive] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [quizPackage, setQuizPackage] = useState(null);

	// Убираем неиспользуемые переменные из store

	const questions = [
		{
			key: 'side',
			title: t('quiz.questions.side.title'),
			type: 'images',
			options: [
				{ key: 'ct', label: t('quiz.questions.side.options.ct'), image: '/assets/images/quiez/ct.png' },
				{ key: 't', label: t('quiz.questions.side.options.t'), image: '/assets/images/quiez/terror.png' }
			]
		},
		{
			key: 'weapon',
			title: t('quiz.questions.weapon.title'),
			type: 'weapon',
			image: '/assets/images/quiez/weapons.png',
			options: [
				{ key: 'ak47', label: t('quiz.questions.weapon.options.ak47') },
				{ key: 'm4a1s', label: t('quiz.questions.weapon.options.m4a1s') },
				{ key: 'awp', label: t('quiz.questions.weapon.options.awp') }
			]
		},
		{
			key: 'map',
			title: t('quiz.questions.map.title'),
			type: 'map',
			image: '/assets/images/quiez/maps.png',
			options: [
				{ key: 'dust2', label: t('quiz.questions.map.options.dust2') },
				{ key: 'mirage', label: t('quiz.questions.map.options.mirage') },
				{ key: 'nuke', label: t('quiz.questions.map.options.nuke') }
			]
		}
	];

	// Загрузка пакетов при монтировании компонента
	useEffect(() => {
		const loadPackages = async () => {
			try {
				// Загружаем пакеты ВКЛЮЧАЯ квиз-пакет
				const packagesData = await apiService.getPackages(false);
				console.log('Загруженные пакеты в квизе:', packagesData);

				// Находим квиз-пакет (с 10 звездами)
				console.log('Ищем пакет с stars === 10...');
				console.log('Все пакеты:', packagesData.map(pkg => ({ id: pkg.id, stars: pkg.stars, tokens: pkg.tokens })));
				const quiz = packagesData.find(pkg => pkg.stars === 10);
				console.log('Найденный квиз-пакет:', quiz);

				if (quiz) {
					setQuizPackage(quiz);
				} else {
					console.error('Квиз-пакет с 10 звездами не найден!');
					setError('Квиз-пакет не найден');
				}
			} catch (err) {
				console.error('Ошибка при загрузке пакетов:', err);
				setError('Ошибка при загрузке пакетов');
			}
		};

		loadPackages();
	}, []); // Убираем зависимости, чтобы избежать бесконечного цикла

	// Таймер для спецпредложения
	useEffect(() => {
		if (!timerActive) return;

		const interval = setInterval(() => {
			setTimeLeft(prev => {
				if (prev <= 0) {
					setTimerActive(false);
					return 0;
				}
				return prev - 10; // Обновляем каждые 10мс для плавности
			});
		}, 10);

		return () => clearInterval(interval);
	}, [timerActive]);

	const handleAnswer = (questionKey, answer) => {
		setAnswers(prev => ({ ...prev, [questionKey]: answer }));
	};

	const handleNext = () => {
		if (currentStep < questions.length) {
			setCurrentStep(prev => prev + 1);
		} else if (currentStep === questions.length) {
			// Переходим к результату
			setCurrentStep(questions.length + 1);
			setTimerActive(true);
		}
	};

	const handleStart = () => {
		setCurrentStep(1);
	};

	const handleTakeOffer = async () => {
		if (!quizPackage) {
			setError('Квиз-пакет не найден');
			return;
		}

		setLoading(true);
		setError(null);

		try {
			// Сначала подтверждаем соглашения
			await apiService.auth();

			const response = await apiService.createInvoiceLink(quizPackage.id);

			if (response.success === false) {
				// Обработка ошибок от API
				if (response.error.includes('время истекло')) {
					setError('Время для покупки квиз-пакета истекло. Вы можете купить его только в течение 5 минут после регистрации.');
				} else if (response.error.includes('уже купили')) {
					setError('Вы уже купили квиз-пакет. Каждый пользователь может купить его только один раз.');
				} else if (response.error.includes('не найден')) {
					setError('Пакет не найден или неактивен');
				} else {
					setError(response.error || 'Ошибка при создании ссылки на оплату');
				}
				return;
			}

			// Если успешно, открываем ссылку на оплату
			const invoiceLink = response.data?.invoice_link || response.invoice_link;
			const transactionId = response.data?.transaction_id || response.transaction_id;

			console.log('Invoice link:', invoiceLink);
			console.log('Transaction ID:', transactionId);

			if (invoiceLink) {
				// Используем WebApp.openInvoice для мобильных устройств
				WebApp.openInvoice(invoiceLink, (status) => {
					console.log('Payment callback status:', status);
					if (status === 'paid') {
						// Сохраняем флаг о прохождении квиза после успешной оплаты
						localStorage.setItem('quizCompleted', 'true');
						console.log('Payment successful! Redirecting to main page...');

						// Отправляем событие в Facebook Analytics
						ReactPixel.track('Purchase', {
							event_type: 'quiz_purchase_front',
							package_id: quizPackage.id,
							package_stars: quizPackage.stars,
							package_tokens: quizPackage.tokens
						});
						console.log('Facebook Analytics event sent: Purchase with event_type: quiz_purchase_front');

						// Обновляем данные пользователя после успешной оплаты
						fetchUser().then(() => {
							console.log('User data updated after payment');
							// Переходим на главную страницу
							onComplete();
						}).catch(err => {
							console.error('Error updating user data:', err);
							// Все равно переходим на главную страницу
							onComplete();
						});
					} else if (status === 'cancelled') {
						console.log('Payment cancelled');
					} else if (status === 'failed') {
						console.log('Payment failed');
					}
				});
			} else {
				setError('Не удалось получить ссылку на оплату');
			}
		} catch (err) {
			console.error('Ошибка при создании ссылки на оплату:', err);
			setError('Ошибка при создании ссылки на оплату');
		} finally {
			setLoading(false);
		}
	};

	const handleLater = () => {
		// Сохраняем флаг о прохождении квиза
		localStorage.setItem('quizCompleted', 'true');
		onComplete();
	};

	const handleShowOtherCases = () => {
		// Сохраняем флаг о прохождении квиза (даже если не купили)
		localStorage.setItem('quizCompleted', 'true');
		onComplete();
	};

	const formatTimer = (milliseconds) => {
		const seconds = Math.floor(milliseconds / 1000);
		const ms = Math.floor((milliseconds % 1000) / 10);
		return `00:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
	};

	const getPersonalizedOffer = () => {
		const { side, weapon, map } = answers;

		// Логика персонализации на основе ответов
		const mapNames = {
			'dust2': 'Dust II',
			'mirage': 'Mirage',
			'nuke': 'Nuke'
		};

		const weaponNames = {
			'ak47': 'AK-47',
			'm4a1s': 'M4A1-S',
			'awp': 'AWP'
		};

		const sideNames = {
			'ct': 'Counter-Terrorists',
			't': 'Terrorists'
		};

		// Пути к картинкам
		const sideImages = {
			'ct': '/assets/images/quiez/ct.png',
			't': '/assets/images/quiez/terror.png'
		};

		const weaponImages = {
			'ak47': '/assets/images/quiez/ak-47.webp',
			'm4a1s': '/assets/images/quiez/m4.webp',
			'awp': '/assets/images/quiez/awp.webp'
		};

		const mapImages = {
			'dust2': '/assets/images/quiez/dust.webp',
			'mirage': '/assets/images/quiez/mirage.jpg',
			'nuke': '/assets/images/quiez/nuke.webp'
		};

		const caseName = map ? `${mapNames[map] || map} Starter Pack` : 'Starter Pack';
		const weaponName = weapon ? weaponNames[weapon] || weapon : 'оружие';
		const originalPrice = 100; // Фиксированная цена до скидки
		const discountedPrice = 10; // Фиксированная цена со скидкой

		// Определяем тему на основе выбранной стороны
		let theme = 't'; // по умолчанию террористы
		if (side === 'ct') {
			theme = 'ct';
		} else if (side === 't') {
			theme = 't';
		}

		return {
			caseName,
			weaponName,
			originalPrice,
			discountedPrice,
			theme,
			selectedItems: [
				{
					type: 'side',
					name: sideNames[side] || 'Unknown',
					image: sideImages[side] || '/assets/images/quiez/terror.png'
				},
				{
					type: 'weapon',
					name: weaponNames[weapon] || 'Unknown',
					image: weaponImages[weapon] || '/assets/images/quiez/ak-47.webp'
				},
				{
					type: 'map',
					name: mapNames[map] || 'Unknown',
					image: mapImages[map] || '/assets/images/quiez/dust.webp'
				}
			]
		};
	};

	const progress = currentStep === 0 ? 0 : (currentStep / questions.length) * 100;

	// Экран приветствия
	if (currentStep === 0) {
		return (
			<Container>
				<Header>
					<Logo>SkinForge</Logo>
				</Header>
				<Content>
					<QuestionSection>
						<WelcomeImage src="/assets/images/quiez/tr_ct.png" alt="Counter-Strike Teams" />
						<Title>{t('quiz.welcome.title')}</Title>
						<Description>{t('quiz.welcome.description')}</Description>
					</QuestionSection>
					<QuizSection>
						<ActionButton onClick={handleStart}>
							{t('quiz.welcome.startButton')}
						</ActionButton>
					</QuizSection>
				</Content>
			</Container>
		);
	}

	// Экран результата
	if (currentStep === questions.length + 1) {
		const offer = getPersonalizedOffer();
		const timerExpired = timeLeft <= 0;

		return (
			<Container>
				<Header>
					<Logo>SkinForge</Logo>
				</Header>
				<Content>
					<QuestionSection>
						<Title>{t('quiz.result.title')}</Title>
						<Description>{t('quiz.result.subtitle')}</Description>

						<TimerContainer>
							<Timer>{formatTimer(timeLeft)}</Timer>
						</TimerContainer>

						<PersonalizedOfferCard theme={offer.theme}>
							<OfferTitle>{offer.caseName}</OfferTitle>
							<OfferSubtitle>{t('quiz.result.subtitle')}</OfferSubtitle>

							<PersonalizedBadge>{t('quiz.result.personalized')}</PersonalizedBadge>

							<SelectedItemsContainer>
								{offer.selectedItems.map((item, index) => (
									<SelectedItem key={index}>
										<SelectedItemImage src={item.image} alt={item.name} type={item.type} />
										<SelectedItemLabel>{item.name}</SelectedItemLabel>
									</SelectedItem>
								))}
							</SelectedItemsContainer>

							<PriceContainer>
								<FragsInfo>
									<span>{quizPackage ? quizPackage.tokens.toLocaleString() : '25,000'}<FragIcon src="/assets/images/quiez/frags.png" alt="frags" /> =</span>
									<OriginalPriceInText>{offer.originalPrice}<StarIcon>⭐</StarIcon></OriginalPriceInText>
									<Arrow>→</Arrow>
									<NewPrice>{offer.discountedPrice}<StarIcon>⭐</StarIcon></NewPrice>
								</FragsInfo>
								<DiscountBadge>-90%</DiscountBadge>
							</PriceContainer>
						</PersonalizedOfferCard>
					</QuestionSection>
					<QuizSection>
						{error && <ErrorMessage>{error}</ErrorMessage>}
						<ButtonContainer>
							{timerExpired ? (
								<ActionButton onClick={handleShowOtherCases}>
									🚀 {t('quiz.result.offer.startGame')}
								</ActionButton>
							) : (
								<ActionButton onClick={handleTakeOffer} disabled={loading}>
									{loading && <LoadingSpinner />}
									{t('quiz.result.offer.takeNowWithPrice')}
								</ActionButton>
							)}
						</ButtonContainer>
					</QuizSection>
				</Content>
			</Container>
		);
	}

	// Экран вопроса
	const currentQuestion = questions[currentStep - 1];
	const selectedAnswer = answers[currentQuestion.key];

	return (
		<Container>
			<Header>
				<Logo>SkinForge</Logo>
				<ProgressBar>
					<ProgressFill progress={progress} />
				</ProgressBar>
				<div style={{ textAlign: 'center', fontSize: '14px', color: '#ccc' }}>
					{t('quiz.progress', { current: currentStep, total: questions.length })}
				</div>
			</Header>

			<Content>
				<QuestionSection>
					<Title>{currentQuestion.title}</Title>

					{currentQuestion.type === 'images' && (
						<ImageContainer columns={currentQuestion.options.length}>
							{currentQuestion.options.map((option) => (
								<ImageWrapper
									key={option.key}
									onClick={() => handleAnswer(currentQuestion.key, option.key)}
								>
									<OptionImage src={option.image} alt={option.label} />
								</ImageWrapper>
							))}
						</ImageContainer>
					)}
					{(currentQuestion.type === 'weapon' || currentQuestion.type === 'map') && (
						<WelcomeImage src={currentQuestion.image} alt={currentQuestion.type === 'weapon' ? 'Weapons' : 'Maps'} />
					)}
				</QuestionSection>

				<QuizSection>
					{(currentQuestion.type === 'images' || currentQuestion.type === 'weapon' || currentQuestion.type === 'map') ? (
						<ImageOptionsContainer>
							{currentQuestion.options.map((option) => (
								<OptionButton
									key={option.key}
									selected={selectedAnswer === option.key}
									onClick={() => handleAnswer(currentQuestion.key, option.key)}
								>
									{option.label}
								</OptionButton>
							))}
						</ImageOptionsContainer>
					) : (
						<OptionsContainer>
							{currentQuestion.options.map((option) => (
								<OptionButton
									key={option.key}
									selected={selectedAnswer === option.key}
									onClick={() => handleAnswer(currentQuestion.key, option.key)}
								>
									{option.label}
								</OptionButton>
							))}
						</OptionsContainer>
					)}

					<ActionButton
						onClick={handleNext}
						disabled={!selectedAnswer}
					>
						{t('quiz.next')}
					</ActionButton>
				</QuizSection>
			</Content>
		</Container>
	);
};

export default OnboardingQuiz;
